import React, { useEffect, useMemo, useState } from 'react';
import WeeklyHeader from './WeeklyHeader';
import WeeklyAnalysisCard from './WeeklyAnalysisCard';
import EmptyReportCard from './EmptyReportCard';
import CardSwiper from './CardSwiper';
import mascot from '../../images/common/img_main_mascot_03.svg';
import style from './ExerciseReport.module.css';

import dayjs from 'dayjs';

import axiosApi from '../../api/daonAxios';

const EMPTY_TITLE = '이번 주 운동 기록이 없어요.';

const formatNumber = (value) => Number(value || 0).toLocaleString('ko-KR');

const ExerciseReport = () => {

    const weeks = useMemo(() => {
        const today = dayjs();

        // 리포트 주는 월~일 기준이다. dayjs 의 startOf('week') 는 일요일 시작이라 쓰지 않는다.
        // today.day() 는 일=0 ~ 토=6. +6 한 뒤 7로 나눈 나머지가 이번 주 월요일부터 지난 날 수다.
        const thisMonday = today.subtract((today.day() + 6) % 7, 'day').startOf('day');

        // 배치가 월요일 새벽에 '막 끝난 주'를 만든다. 이번 주는 아직 리포트가 없으므로 지난주가 마지막 칸이다.
        const lastMonday = thisMonday.subtract(1, 'week');

        return Array.from({ length: 12 }, (_, index) => {
            const start = lastMonday.subtract(11 - index, 'week');
            const end = start.add(6, 'day');

            return {
                startDe: start.format('YYYYMMDD'),
                endDe: end.format('YYYYMMDD'),
                range: `${start.format('YY.MM.DD')} ~ ${end.format('YY.MM.DD')}`,
            };
        });
    }, []);

    const [weekIdx, setWeekIdx] = useState(weeks.length - 1);   // 마지막 칸 = 막 끝난 주

    const week = weeks[weekIdx];

    const [weekNo, setWeekNo] = useState(null);        // 가입한 주가 1주차. 아직 모르면 null
    const [exercise, setExercise] = useState(null);    // 주간 운동 분석. 그 주 리포트가 없으면 null
    const [aiAsstMsg, setAiAsstMsg] = useState(null);  // AI 문구. 아직 분석 전이면 null

    // 다음주 운동 추천. AI 가 주기로 되어 있으나 형식이 정해지지 않아 아직 늘 빈 목록이다
    const [recommendations, setRecommendations] = useState([]);

    useEffect(() => {

        if (!week) {
            return;
        }

        const fetchExerciseData = async () => {
            try {
                const jToken = localStorage.getItem('jToken');

                const response = await axiosApi.get('/report/exercise', {
                    params: {
                        startDe: week.startDe,
                        endDe: week.endDe,
                    },
                    headers: {
                        Authorization: `Bearer ${jToken}`,
                    },
                });

                const result = response.data?.result || {};

                setWeekNo(result.weekNo ?? null);
                setExercise(result.exercise || null);
                setAiAsstMsg(result.aiAsstMsg || null);
                setRecommendations(result.recommendations || []);

            } catch (error) {
                console.error(error);

                setWeekNo(null);
                setExercise(null);
                setAiAsstMsg(null);
                setRecommendations([]);
            }
        };

        fetchExerciseData();

    }, [week]);

    // 운동한 날이 하루도 없으면 빈 카드를 보여준다
    const hasExercise = (exercise?.recDayCo || 0) > 0;

    const durationDaily = useMemo(() => exercise?.duration?.daily || [], [exercise]);
    const calorieDaily  = useMemo(() => exercise?.calorie?.daily  || [], [exercise]);

    /*
     * AI 문구는 한 덩이로 내려온다. 첫 줄을 제목, 나머지를 본문으로 나눈다.
     * 기록이 하나도 없는 주는 AI 가 쓸 내용이 없어 문구가 오지 않는다.
     * 그때는 화면이 직접 안내 문구를 넣는다. 카드를 숨기면 주차와 날짜만 남아
     * 무엇을 해야 하는지 알 수 없다.
     */
    const aiMessage = useMemo(() => {
        if (aiAsstMsg) {
            const [firstLine, ...restLines] = aiAsstMsg.split('\n');

            return { title: firstLine, desc: restLines.join('\n') };
        }

        if (!hasExercise) {
            return {
                title: '다시 웰비와 시작해 볼까요? ✨',
                desc : '• 지난주 운동 기록이 없어 주간 운동 분석을 생성하지 못했어요. 📊\n'
                     + '• 나에게 딱 맞는 맞춤 운동 추천을 받기 위해 이번 주에는 기록을 차곡차곡 채워볼까요? 🎯',
            };
        }

        return null;
    }, [aiAsstMsg, hasExercise]);

    return (
        <div className={style.wrap}>
            <WeeklyHeader
                weekNum={weekNo}
                dateRange={week.range}
                message={aiMessage}
                mascotImg={mascot}
                onPrev={() => setWeekIdx(i => i - 1)}
                onNext={() => setWeekIdx(i => i + 1)}
                hasPrev={weekIdx > 0 && (weekNo == null || weekNo > 1)}
                hasNext={weekIdx < weeks.length - 1}
            />

            {/* 주간 운동 분석 */}
            <h3 className={style.sectionTitle}>주간 운동 분석</h3>
            {hasExercise ? (
                <CardSwiper slidesPerView={1.08}>
                    <WeeklyAnalysisCard
                        title="평균 운동시간"
                        value={formatNumber(exercise.duration.avg)}
                        unit="분"
                        dailyValues={durationDaily}
                        maxValue={exercise.duration.max}
                        threshold={exercise.duration.threshold}
                        // highlightOver
                        warnWhen="under"
                    />
                    <WeeklyAnalysisCard
                        title="평균 소모 칼로리"
                        value={formatNumber(exercise.calorie.avg)}
                        unit="Kcal"
                        dailyValues={calorieDaily}
                        maxValue={exercise.calorie.max}
                        threshold={exercise.calorie.threshold}
                        // highlightOver
                         warnWhen="under"
                    />
                </CardSwiper>
            ) : (
                <EmptyReportCard
                    title={EMPTY_TITLE}
                    desc="운동을 기록하고 분석을 확인하세요!"
                />
            )}

            {/* 다음주 운동 추천 */}
            <h3 className={style.sectionTitle}>다음주 운동 추천</h3>
            {recommendations.length > 0 ? (
                <CardSwiper slidesPerView={1.1} spaceBetween={12}>
                    {recommendations.map((r, i) => (
                        <div key={i} className={style.recCard}>
                            <div className={style.recTop}>
                                <span className={style.recRank}>{i + 1}</span>
                                <div className={style.recRight}>
                                    <div className={style.top}>
                                        <strong className={style.recName}>{r.name}</strong>
                                        <span className={style.recInfo}>{r.duration} {r.kcal}</span>
                                    </div>

                                    <div className={style.bot}>
                                        <p className={style.recDesc}>{r.desc}</p>
                                    </div>
                                </div>

                            </div>

                        </div>
                    ))}
                </CardSwiper>
            ) : (
                <EmptyReportCard
                    title="아직 추천이 준비되지 않았어요."
                    desc="운동을 기록하면 추천을 받을 수 있어요!"
                />
            )}
        </div>
    );
};

export default ExerciseReport;
